/**
 * Schedule View Component
 *
 * Scrolling schedule showing all dates with all tours.
 * Shares search/filter state with the map view.
 * Groups stops by month and day for easy browsing.
 *
 * @element peach-schedule-view
 *
 * @fires location-selected - When user selects a location
 */

import { LitElement, html } from "lit";
import { customElement, state, query } from "lit/decorators.js";
import { locationStore } from "@/features/locations/stores/location-store";
import {
  aggregateCalendarDates,
  filterFutureDates,
  groupByMonth,
  type ScheduleMonth,
} from "../services/calendar-data";
import "./schedule-month";
import "@/features/ui/icon";

/**
 * Number of days to render initially (keeps first paint fast)
 */
const INITIAL_DAYS = 10;

/**
 * Number of additional days to load when scrolling
 */
const LOAD_MORE_COUNT = 15;

/**
 * Scroll threshold (px from bottom) to trigger loading more
 */
const SCROLL_THRESHOLD = 300;

@customElement("peach-schedule-view")
export class ScheduleViewComponent extends LitElement {
  createRenderRoot() {
    return this;
  }

  /**
   * Grouped schedule data by month
   */
  @state()
  declare private scheduleMonths: ScheduleMonth[];

  /**
   * Search point for distance calculation (drives visible location filtering)
   */
  @state()
  declare private searchPoint: { lat: number; lng: number } | null;

  /**
   * Loading state
   */
  @state()
  declare private isLoading: boolean;

  /**
   * Total stop count
   */
  @state()
  declare private totalStops: number;

  /**
   * Number of days currently rendered (progressive loading)
   */
  @state()
  declare private renderedDayCount: number;

  /**
   * Total days available across all months
   */
  private totalDayCount = 0;

  /**
   * Reference to scroll container
   */
  @query(".calendar-scroll-container")
  private scrollContainer?: HTMLElement;

  /**
   * Bound scroll handler for cleanup
   */
  private boundScrollHandler: () => void;

  /**
   * Track pending scroll updates
   */
  private scrollUpdatePending = false;

  private unsubscribe?: () => void;

  constructor() {
    super();
    this.scheduleMonths = [];
    this.searchPoint = null;
    this.isLoading = true;
    this.totalStops = 0;
    this.renderedDayCount = INITIAL_DAYS;
    this.boundScrollHandler = this.handleScroll.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.subscribeToLocationStore();
    // Force initial data load in case subscription didn't fire
    this.updateScheduleData();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.scrollContainer?.removeEventListener("scroll", this.boundScrollHandler);
  }

  /**
   * Attach scroll listener when container is available
   */
  protected updated() {
    const container = this.scrollContainer ?? this.querySelector(".calendar-scroll-container");
    if (container && !container.hasAttribute("data-scroll-attached")) {
      container.setAttribute("data-scroll-attached", "true");
      container.addEventListener("scroll", this.boundScrollHandler, { passive: true });
    }
  }

  /**
   * Handle scroll for progressive loading
   */
  private handleScroll() {
    if (this.scrollUpdatePending) return;
    if (this.renderedDayCount >= this.totalDayCount) return;

    this.scrollUpdatePending = true;
    requestAnimationFrame(() => {
      this.checkLoadMore();
      this.scrollUpdatePending = false;
    });
  }

  /**
   * Check if we need to load more days
   */
  private checkLoadMore() {
    const container = this.scrollContainer ?? this.querySelector(".calendar-scroll-container");
    if (!container) return;

    const scrollBottom = container.scrollTop + container.clientHeight;
    const scrollHeight = container.scrollHeight;

    if (scrollHeight - scrollBottom < SCROLL_THRESHOLD) {
      this.renderedDayCount = Math.min(
        this.renderedDayCount + LOAD_MORE_COUNT,
        this.totalDayCount
      );
    }
  }

  /**
   * Subscribe to location store for filtered data
   *
   * @remarks
   * Subscribes to visibleLocationIds (not tours/markets directly) to ensure
   * we see the correctly filtered data. This avoids a race condition where
   * searchPoint updates before visibleLocationIds is recalculated.
   *
   * The pattern matches locations-list.ts and map-canvas.ts which also
   * subscribe to visibleLocationIds for consistent behavior.
   */
  private subscribeToLocationStore() {
    this.unsubscribe = locationStore.subscribe(
      (state) => ({
        visibleLocationIds: state.visibleLocationIds,
        searchPoint: state.searchPoint,
        isLoading: state.isLoading,
      }),
      (selected) => {
        this.searchPoint = selected.searchPoint;
        this.updateScheduleData();
      },
      {
        fireImmediately: true,
        equalityFn: (a, b) =>
          a.visibleLocationIds === b.visibleLocationIds &&
          a.searchPoint === b.searchPoint &&
          a.isLoading === b.isLoading,
      }
    );
  }

  /**
   * Update schedule data from visible locations
   *
   * @remarks
   * Always uses getVisibleLocations() which respects IP geolocation filtering.
   * This matches the map view behavior and prevents rendering all 362+ locations
   * before the IP filter is applied.
   */
  private updateScheduleData() {
    const locations = locationStore.getState().getVisibleLocations();

    if (locations.length === 0) {
      this.isLoading = locationStore.getState().isLoading;
      this.scheduleMonths = [];
      this.totalStops = 0;
      this.totalDayCount = 0;
      return;
    }

    // Aggregate and group by month
    const allDates = aggregateCalendarDates(locations);
    const futureDates = filterFutureDates(allDates);
    this.scheduleMonths = groupByMonth(futureDates);
    this.isLoading = false;

    // Count total days and stops
    this.totalDayCount = this.scheduleMonths.reduce((sum, m) => sum + m.days.length, 0);
    this.totalStops = futureDates.reduce((sum, d) => sum + d.stops.length, 0);

    // Reset rendered count for new data
    this.renderedDayCount = INITIAL_DAYS;
  }

  /**
   * Get months with days sliced to the rendered count
   *
   * @remarks
   * Progressive loading: only renders first N days across all months,
   * preventing browser lock-up on initial load.
   */
  private getVisibleMonths(): ScheduleMonth[] {
    let remainingDays = this.renderedDayCount;
    const visibleMonths: ScheduleMonth[] = [];

    for (const month of this.scheduleMonths) {
      if (remainingDays <= 0) break;

      if (month.days.length <= remainingDays) {
        // Include entire month
        visibleMonths.push(month);
        remainingDays -= month.days.length;
      } else {
        // Include partial month
        visibleMonths.push({
          ...month,
          days: month.days.slice(0, remainingDays),
        });
        remainingDays = 0;
      }
    }

    return visibleMonths;
  }

  /**
   * Handle location selection from a schedule stop
   *
   * @remarks
   * Selects the location in the store (updates map marker, desktop panel),
   * then bubbles the event so the parent (local-pickup-view) can open
   * the details bottom sheet on mobile.
   */
  private handleLocationSelected(e: CustomEvent) {
    const { locationId, stopId } = e.detail;

    locationStore.getState().selectLocation(locationId);

    this.dispatchEvent(
      new CustomEvent("location-selected", {
        detail: { locationId, stopId },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Clear search and show all locations
   */
  private handleClearSearch() {
    locationStore.getState().setSearchPoint(null);
    locationStore.getState().updateFilters({ searchQuery: "" });
  }

  private renderLoading() {
    return html`
      <div class="flex items-center justify-center h-full min-h-[400px] bg-cream">
        <div class="text-center">
          <div
            class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-2"
          ></div>
          <p class="text-sm text-muted">Loading schedule...</p>
        </div>
      </div>
    `;
  }

  private renderEmpty() {
    const hasSearch = !!this.searchPoint;

    return html`
      <div class="flex items-center justify-center h-full min-h-[400px] bg-surface px-4">
        <div class="text-center max-w-sm">
          <peach-icon
            name="calendar"
            size="xl"
            class="text-muted mx-auto mb-4"
          ></peach-icon>
          ${hasSearch
        ? html`
                <h3 class="font-semibold text-default mb-2">
                  No locations found near you
                </h3>
                <p class="text-sm text-muted mb-4">
                  Try searching a different area or clear the search to see all locations.
                </p>
                <button
                  @click=${this.handleClearSearch}
                  class="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand transition-colors"
                >
                  Show All Locations
                </button>
              `
        : html`
                <h3 class="font-semibold text-default mb-2">
                  No upcoming dates
                </h3>
                <p class="text-sm text-muted">
                  Check back soon for new tour dates!
                </p>
              `}
        </div>
      </div>
    `;
  }

  render() {
    const visibleMonths = this.getVisibleMonths();
    const hasMore = this.renderedDayCount < this.totalDayCount;

    return html`
      <div class="flex flex-col h-full bg-cream overflow-hidden" @location-selected=${this.handleLocationSelected}>
        <div class="calendar-scroll-container flex-1 overflow-y-auto">
          ${this.isLoading
        ? this.renderLoading()
        : this.scheduleMonths.length === 0
          ? this.renderEmpty()
          : html`
                  <div class="pb-24 bg-cream min-h-full">
                    ${visibleMonths.map(
            (month) => html`
                        <peach-schedule-month
                          .month=${month}
                          .searchPoint=${this.searchPoint}
                        ></peach-schedule-month>
                      `
          )}
                    ${hasMore ? html`
                      <div class="flex justify-center py-8">
                        <div class="animate-pulse text-sm text-muted">Loading more dates...</div>
                      </div>
                    ` : null}
                  </div>
                `}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "peach-schedule-view": ScheduleViewComponent;
  }
}
